import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import { Card } from '@/components/ui';
import { listUpcomingDeadlines, type UpcomingDeadline } from '@/lib/api-client';
import { formatDueDate, relativeDue, urgencyClasses, urgencyOf } from '@/lib/deadlines';

// Cross-matter upcoming deadlines widget (Sprint P5 Section 4.5). Shown on the
// matters/home and (compact) on the hearing screen. Hides itself when there is
// nothing upcoming, so it never adds noise.
export function UpcomingDeadlines({ compact = false }: { compact?: boolean }) {
  const [items, setItems] = useState<UpcomingDeadline[] | null>(null);

  useEffect(() => {
    let active = true;
    listUpcomingDeadlines()
      .then((d) => active && setItems(d))
      .catch(() => active && setItems([]));
    return () => {
      active = false;
    };
  }, []);

  if (!items || items.length === 0) {
    return null;
  }
  const shown = compact ? items.slice(0, 4) : items.slice(0, 8);

  return (
    <Card variant="panel" padding="md" className="space-y-2">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-amber-500" />
        <h2 className="text-[10px] uppercase tracking-[0.2em] text-stone-400">Upcoming deadlines</h2>
      </div>
      <ul className="space-y-1.5">
        {shown.map((d) => {
          const urgency = urgencyOf(d.dueDate, d.status);
          return (
            <li key={d.id}>
              <Link
                to={`/matters/${d.matterId}/deadlines`}
                className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors hover:border-stone-600 ${urgencyClasses(urgency)}`}
              >
                <span className="min-w-0 flex-1 truncate">{d.description}</span>
                <span className="shrink-0 font-medium">{formatDueDate(d.dueDate).replace(/ \d{4}$/, '')}</span>
                <span className="shrink-0 uppercase tracking-[0.1em] opacity-80">
                  {relativeDue(d.dueDate, d.status)}
                </span>
              </Link>
              {!compact ? (
                <p className="mt-0.5 pl-2.5 text-[10px] uppercase tracking-[0.15em] text-stone-600">
                  {d.matter.name}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
